import { useEffect, useRef, useState, useCallback } from 'react'
import { Image as ImageIcon, Plus, X, Download, Pencil, Trash2, Upload, Share2 } from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal, confirmModal } from '../components/ui'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }
const AREA_TIPOS = ['foto', 'nome', 'whatsapp', 'instagram', 'link', 'cta', 'texto']
const FORMATOS = { post: { w: 1080, h: 1080, ar: '1 / 1' }, story: { w: 1080, h: 1920, ar: '9 / 16' } }

export default function Flyer() {
  const isAdmin = (load('user')?.perfil || '') === 'admin'
  const [templates, setTemplates] = useState([])
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(true)
  const [edit, setEdit] = useState(null)        // admin: template em edição

  const carregar = useCallback(() => {
    setLoading(true)
    const ps = [api.get('/flyer/templates').then((r) => setTemplates(r.data.data || [])).catch(() => setTemplates([]))]
    if (!isAdmin) ps.push(api.get('/flyer/dados').then((r) => setDados(r.data)).catch(() => setDados(null)))
    Promise.all(ps).finally(() => setLoading(false))
  }, [isAdmin])
  useEffect(() => { carregar() }, [carregar])

  const del = async (id) => { if (await confirmModal('Excluir este modelo?')) api.delete(`/flyer/templates/${id}`).then(carregar).catch(() => errorModal('Erro.')) }

  if (loading) return <div className="flex justify-center py-16"><Loader text="Carregando modelos..." /></div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow"><ImageIcon size={20} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">Gerador de Flyers</h1>
            <p className="text-sm text-gray-500">{isAdmin ? 'Cadastre modelos aprovados com áreas para foto, nome, WhatsApp e CTA.' : 'Escolha um modelo, envie sua foto e gere sua arte pronta para redes sociais.'}</p>
          </div>
        </div>
        {isAdmin && <button onClick={() => setEdit({})} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold"><Plus size={16} /> Novo Modelo</button>}
      </div>

      {templates.length === 0 ? (
        <p className="text-gray-400 text-sm py-10 text-center">Nenhum modelo {isAdmin ? '— clique em "Novo Modelo".' : 'disponível ainda.'}</p>
      ) : isAdmin ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gray-100 flex items-center justify-center" style={{ aspectRatio: FORMATOS[t.formato]?.ar || '1 / 1' }}>
                {t.imagem_base ? <img src={t.imagem_base} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={32} className="text-gray-300" />}
              </div>
              <div className="p-3">
                <p className="font-semibold text-sm text-gray-900 truncate">{t.nome}</p>
                <p className="text-[11px] text-gray-400">{t.categoria} · {t.formato} · {(t.areas || []).length} áreas</p>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => setEdit(t)} className="flex-1 bg-gray-100 rounded-lg py-1 text-xs font-semibold flex items-center justify-center gap-1"><Pencil size={12} /> Editar</button>
                  <button onClick={() => del(t.id)} className="bg-red-50 text-red-500 rounded-lg px-2"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Gerador templates={templates} dados={dados} />
      )}

      {edit && <TemplateForm template={edit} onClose={() => setEdit(null)} onSalvo={() => { setEdit(null); carregar() }} />}
    </div>
  )
}

// ───────── Licenciado: gerar arte ─────────
function Gerador({ templates, dados }) {
  const [sel, setSel] = useState(templates[0] || null)
  const [foto, setFoto] = useState(null)
  const [campos, setCampos] = useState({
    nome: dados?.nome || '', whatsapp: '', instagram: '', link: dados?.codigo_indicacao ? `theconect.com.br/${dados.codigo_indicacao}` : '', cta: 'Fale comigo!', texto: '',
  })
  const ref = useRef(null)
  const set = (k) => (e) => setCampos({ ...campos, [k]: e.target.value })
  const fmt = FORMATOS[sel?.formato] || FORMATOS.post
  const RENDER_W = sel?.formato === 'story' ? 360 : 480

  const fotoChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return
    const rd = new FileReader(); rd.onload = () => setFoto(rd.result); rd.readAsDataURL(file)
  }
  const baixar = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(ref.current, { useCORS: true, scale: fmt.w / RENDER_W, backgroundColor: null })
      const a = document.createElement('a'); a.href = canvas.toDataURL('image/png')
      a.download = `flyer-${dados?.codigo_indicacao || 'theconect'}.png`; a.click()
      successModal('Arte gerada! Compartilhe nas suas redes. 🎨')
    } catch { errorModal('Não foi possível gerar a imagem.') }
  }
  const areasTipos = new Set((sel?.areas || []).map((a) => a.tipo))

  if (!sel) return null
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Painel de edição */}
      <div className="space-y-3">
        <div>
          <p className="text-xs text-gray-500 mb-1">Modelo</p>
          <div className="flex gap-2 flex-wrap">
            {templates.map((t) => (
              <button key={t.id} onClick={() => setSel(t)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${sel.id === t.id ? 'border-accent-500' : 'border-transparent'}`}>
                {t.imagem_base ? <img src={t.imagem_base} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-100" />}
              </button>
            ))}
          </div>
        </div>
        {areasTipos.has('foto') && (
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer bg-white border border-gray-200 rounded-xl px-3 py-2">
            <Upload size={15} /> {foto ? 'Trocar foto' : 'Enviar sua foto'}
            <input type="file" accept="image/*" className="hidden" onChange={fotoChange} />
          </label>
        )}
        {['nome', 'whatsapp', 'instagram', 'link', 'cta', 'texto'].filter((k) => areasTipos.has(k)).map((k) => (
          <input key={k} value={campos[k]} onChange={set(k)} placeholder={k.charAt(0).toUpperCase() + k.slice(1)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        ))}
        <button onClick={baixar} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"><Download size={16} /> Baixar arte (PNG)</button>
      </div>

      {/* Preview / compositing */}
      <div className="flex justify-center">
        <div ref={ref} className="relative overflow-hidden rounded-xl shadow-lg bg-gray-200" style={{ width: RENDER_W, aspectRatio: fmt.ar }}>
          {sel.imagem_base && <img src={sel.imagem_base} alt="" crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-cover" />}
          {(sel.areas || []).map((a, i) => {
            const style = {
              position: 'absolute', left: `${a.x || 0}%`, top: `${a.y || 0}%`,
              width: `${a.w || 30}%`, textAlign: a.align || 'center',
              color: a.color || '#fff', fontSize: `${(a.font || 5) * RENDER_W / 100}px`,
              fontWeight: 700, lineHeight: 1.1,
            }
            if (a.tipo === 'foto') {
              return <div key={i} style={{ ...style, aspectRatio: '1 / 1', borderRadius: '50%', overflow: 'hidden', background: '#0003' }}>
                {foto && <img src={foto} alt="" className="w-full h-full object-cover" />}
              </div>
            }
            const val = a.tipo === 'whatsapp' ? (campos.whatsapp ? '📱 ' + campos.whatsapp : '')
              : a.tipo === 'instagram' ? (campos.instagram ? '@' + campos.instagram.replace('@', '') : '')
                : campos[a.tipo] || ''
            return <div key={i} style={style}>{val}</div>
          })}
        </div>
      </div>
    </div>
  )
}

// ───────── Admin: cadastrar modelo + áreas ─────────
function TemplateForm({ template, onClose, onSalvo }) {
  const ed = !!template.id
  const [f, setF] = useState({ nome: template.nome || '', categoria: template.categoria || 'flyer', formato: template.formato || 'post', imagem_base: template.imagem_base || '' })
  const [areas, setAreas] = useState(template.areas || [{ tipo: 'nome', x: 10, y: 78, w: 80, font: 6, color: '#ffffff', align: 'center' }])
  const [saving, setSaving] = useState(false)
  const fmt = FORMATOS[f.formato] || FORMATOS.post

  const baseChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return
    const rd = new FileReader(); rd.onload = () => setF({ ...f, imagem_base: rd.result }); rd.readAsDataURL(file)
  }
  const addArea = () => setAreas([...areas, { tipo: 'texto', x: 10, y: 10, w: 50, font: 5, color: '#ffffff', align: 'center' }])
  const setArea = (i, k, v) => setAreas(areas.map((a, j) => (j === i ? { ...a, [k]: v } : a)))
  const delArea = (i) => setAreas(areas.filter((_, j) => j !== i))
  const salvar = () => {
    if (!f.nome.trim()) return errorModal('Informe o nome do modelo.')
    setSaving(true)
    const body = { ...f, areas: areas.map((a) => ({ ...a, x: Number(a.x), y: Number(a.y), w: Number(a.w), font: Number(a.font) })) }
    const req = ed ? api.put(`/flyer/templates/${template.id}`, body) : api.post('/flyer/templates', body)
    req.then(() => { successModal('Modelo salvo.'); onSalvo() }).catch(() => errorModal('Erro ao salvar.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="text-lg font-heading font-bold">{ed ? 'Editar' : 'Novo'} Modelo de Arte</h2><button onClick={onClose}><X size={18} className="text-gray-400" /></button></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} placeholder="Nome do modelo *" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <select value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
                {['flyer', 'story', 'post', 'capa', 'banner', 'convite', 'campanha'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={f.formato} onChange={(e) => setF({ ...f, formato: e.target.value })} className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
                <option value="post">Post 1080×1080</option><option value="story">Story 1080×1920</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <Upload size={15} /> {f.imagem_base ? 'Trocar imagem base' : 'Enviar imagem base (arte aprovada)'}
              <input type="file" accept="image/*" className="hidden" onChange={baseChange} />
            </label>
            <div className="flex items-center justify-between"><p className="text-xs font-bold uppercase text-gray-400">Áreas</p><button onClick={addArea} className="text-xs text-accent-600 font-semibold flex items-center gap-1"><Plus size={13} /> Área</button></div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {areas.map((a, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-2 space-y-1.5">
                  <div className="flex items-center gap-1">
                    <select value={a.tipo} onChange={(e) => setArea(i, 'tipo', e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1 text-xs flex-1">
                      {AREA_TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input type="color" value={a.color || '#ffffff'} onChange={(e) => setArea(i, 'color', e.target.value)} className="w-8 h-7 rounded" />
                    <select value={a.align || 'center'} onChange={(e) => setArea(i, 'align', e.target.value)} className="border border-gray-200 rounded-lg px-1 py-1 text-xs"><option value="left">esq</option><option value="center">centro</option><option value="right">dir</option></select>
                    <button onClick={() => delArea(i)}><Trash2 size={13} className="text-red-400" /></button>
                  </div>
                  <div className="grid grid-cols-4 gap-1 text-[10px] text-gray-400">
                    {['x', 'y', 'w', 'font'].map((k) => (
                      <label key={k}>{k}%<input type="number" value={a[k]} onChange={(e) => setArea(i, k, e.target.value)} className="w-full border border-gray-200 rounded px-1 py-0.5 text-xs text-gray-700" /></label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar Modelo'}</button>
          </div>
          {/* preview do modelo */}
          <div className="flex justify-center items-start">
            <div className="relative overflow-hidden rounded-xl shadow bg-gray-200" style={{ width: f.formato === 'story' ? 220 : 300, aspectRatio: fmt.ar }}>
              {f.imagem_base && <img src={f.imagem_base} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              {areas.map((a, i) => {
                const W = f.formato === 'story' ? 220 : 300
                const st = { position: 'absolute', left: `${a.x}%`, top: `${a.y}%`, width: `${a.w}%`, textAlign: a.align || 'center', color: a.color || '#fff', fontSize: `${(a.font || 5) * W / 100}px`, fontWeight: 700, lineHeight: 1.1, outline: '1px dashed #f59e0b66' }
                if (a.tipo === 'foto') return <div key={i} style={{ ...st, aspectRatio: '1/1', borderRadius: '50%', background: '#0002' }} />
                return <div key={i} style={st}>{a.tipo}</div>
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
